import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, FileText, Trash2, Eye, RefreshCw, Settings, 
  AlertTriangle, HardDrive, Calendar, Download, Eraser
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ enabled: false, days: 30 });
  const [showSettings, setShowSettings] = useState(false);
  const [showLogContent, setShowLogContent] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logContent, setLogContent] = useState('');
  const [cleanupDays, setCleanupDays] = useState(30);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchSettings();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/logs/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setCleanupDays(response.data.days || 30);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleViewLog = async (log) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/logs/content/${encodeURIComponent(log.path)}?lines=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLogContent(response.data.content);
      setSelectedLog(log);
      setShowLogContent(true);
    } catch (error) {
      toast.error('Failed to load log content');
    }
  };

  const handleDeleteLog = async (log) => {
    if (!window.confirm(`Are you sure you want to delete ${log.name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/admin/logs/${encodeURIComponent(log.path)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Log file deleted');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to delete log file');
    }
  };

  const handleEmptyLog = async (log) => {
    if (!window.confirm(`Empty the contents of ${log.name}? The file will remain but all content will be cleared.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/logs/empty/${encodeURIComponent(log.path)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Log file emptied');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to empty log file');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm(`Delete all logs older than ${cleanupDays} days?`)) {
      return;
    }

    setCleaning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/logs/cleanup?days=${cleanupDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${response.data.deleted_count} log files`);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to cleanup logs');
    } finally {
      setCleaning(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/logs/settings?enabled=${settings.enabled}&days=${settings.days}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Settings updated successfully');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTotalSize = () => {
    const total = logs.reduce((sum, log) => sum + log.size, 0);
    return formatFileSize(total);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-outfit text-gray-600">Loading logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-playfair text-4xl font-bold text-primary mb-2">
                System Logs
              </h1>
              <p className="text-gray-600">
                Manage and monitor application log files
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={fetchLogs} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setShowSettings(true)} variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Auto-Delete Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Files</p>
                <p className="text-2xl font-bold text-gray-800">{logs.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <HardDrive className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Size</p>
                <p className="text-2xl font-bold text-gray-800">{getTotalSize()}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Auto-Delete</p>
                <p className="text-2xl font-bold text-gray-800">
                  {settings.enabled ? `${settings.days}d` : 'Off'}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-sm mb-2 block">Quick Cleanup</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(parseInt(e.target.value))}
                  min="1"
                  max="365"
                  className="w-20"
                />
                <Button
                  onClick={handleCleanup}
                  disabled={cleaning}
                  size="sm"
                  variant="destructive"
                >
                  {cleaning ? 'Cleaning...' : `Clean ${cleanupDays}d+`}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Log Files Table */}
        <Card className="p-6">
          <h2 className="font-semibold text-xl mb-4">Log Files</h2>
          
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No log files found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">File Name</th>
                    <th className="text-left p-3 font-semibold">Directory</th>
                    <th className="text-left p-3 font-semibold">Size</th>
                    <th className="text-left p-3 font-semibold">Modified</th>
                    <th className="text-right p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="font-mono text-sm">{log.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {log.directory}
                      </td>
                      <td className="p-3 text-sm">
                        {formatFileSize(log.size)}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {formatDate(log.modified)}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => handleViewLog(log)}
                            size="sm"
                            variant="outline"
                            title="View log content"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleEmptyLog(log)}
                            size="sm"
                            variant="outline"
                            title="Empty log file"
                            className="text-orange-600 hover:text-orange-700 hover:border-orange-600"
                          >
                            <Eraser className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteLog(log)}
                            size="sm"
                            variant="destructive"
                            title="Delete log file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Settings Dialog */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Auto-Delete Settings</DialogTitle>
              <DialogDescription>
                Configure automatic deletion of old log files
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Enable Auto-Delete</Label>
                  <p className="text-sm text-gray-500">
                    Automatically delete old log files
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, enabled: checked })
                  }
                />
              </div>

              <div>
                <Label>Delete logs older than (days)</Label>
                <Input
                  type="number"
                  value={settings.days}
                  onChange={(e) =>
                    setSettings({ ...settings, days: parseInt(e.target.value) })
                  }
                  min="1"
                  max="365"
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Logs older than {settings.days} days will be automatically deleted
                </p>
              </div>

              {settings.enabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        Auto-delete is enabled
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        Log files will be automatically deleted daily at midnight
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateSettings}>
                Save Settings
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Log Content Dialog */}
        <Dialog open={showLogContent} onOpenChange={setShowLogContent}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>
                {selectedLog?.name}
              </DialogTitle>
              <DialogDescription>
                Last 500 lines
              </DialogDescription>
            </DialogHeader>

            <div className="bg-black text-green-400 p-4 rounded-lg overflow-auto max-h-[60vh] font-mono text-sm">
              <pre className="whitespace-pre-wrap">{logContent}</pre>
            </div>

            <div className="flex gap-2 justify-end">
              <Button onClick={() => setShowLogContent(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
